import React, { useRef, useState, useCallback, useEffect } from 'react'

// components
import '@aytm/app/swidget/base/questions/Ecommerce/Ecommerce/Store/ModelViewer.ts'
import { TabView, TabPanel, TabViewTabChangeParams } from 'primereact/tabview'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink.tsx'

// types
import {
  ASSETS_TYPES,
  assetsCollectionElement,
  IEcommerceAnswerSchema
} from '@aytm/app/swidget/base/questions/Ecommerce/types'
import { IImageAsset } from '@aytm/app/swidget/base/modules/images/types'

// functions
import { getAssetType } from '@aytm/app/swidget/base/questions/Ecommerce/utils/getAssetType.ts'
import { getBigAssetPath, getSmallAssetPath } from '@aytm/lib/common/assets'
import AssetsGate from '@aytm/app/swidget/base/questions/Ecommerce/components/AssetsGate.tsx'
import { PlateSkeleton } from './Skeleton'

// hooks
import useOnClickOutside from '@aytm/lib/hooks/useOnClickOutside'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline'
import useAssetsModalData from '@aytm/app/swidget/base/questions/Ecommerce/hooks/useAssetsModalData.ts'

// styles
import style from './AssetsModal.module.scss'

export interface IAssetsModalProps {
  answer: IEcommerceAnswerSchema
  assetsCollection: assetsCollectionElement[]
  currentIndex: number
  onModalClose: (assetType: ASSETS_TYPES) => void
}

const AssetsModal = ({ answer, assetsCollection, currentIndex, onModalClose }: IAssetsModalProps) => {
  // refs
  const dialogRef = useRef<HTMLDivElement>(null)
  const playingIndexRef = useRef<number | null>(null)

  // memoized
  const modalData = useAssetsModalData(assetsCollection, currentIndex)

  // state
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(modalData.isImage ? modalData.separatedIndex : 0)
  const [selectedVideoIndex, setSelectedVideoIndex] = useState<number>(modalData.isVideo ? modalData.separatedIndex : 0)
  const [position, setPosition] = useState({ x: 50, y: 50 })
  const [onImage, setOnImage] = useState<boolean>(false)
  const [currentTabIndex, setCurrenTabIndex] = useState<number>(modalData.activeTab)

  const tabAssetType = (tabIndex: number) =>
    0 === tabIndex ? ASSETS_TYPES.IMAGE : 1 === tabIndex ? ASSETS_TYPES.VIDEO : ASSETS_TYPES.MODEL
  const activeTabAssetType = tabAssetType(currentTabIndex)

  // custom hooks calls
  const timeline = useTimeline()
  const viewport = useViewport()
  const { t } = useSurveyLocale()
  useOnClickOutside(dialogRef, () => {
    onModalClose(activeTabAssetType)
  })

  // functions
  const slide = useCallback(
    (qnt: -1 | 1, tab: 'videos' | 'images') => {
      if ('images' === tab) {
        setSelectedImageIndex(prev => {
          const nextIndex = prev + qnt
          timeline.log({
            ref: answer.id,
            type: 'show_asset',
            target: assetsCollection.indexOf(modalData.images[nextIndex]).toString(),
            context: 'image'
          })
          return nextIndex >= 0 && nextIndex < modalData.images.length ? nextIndex : prev
        })
      } else {
        flushPlayingVideo()
        setSelectedVideoIndex(prev => {
          const nextIndex = prev + qnt
          timeline.log({
            ref: answer.id,
            type: 'show_asset',
            target: assetsCollection.indexOf(modalData.videos[nextIndex]).toString(),
            context: 'video'
          })
          return nextIndex >= 0 && nextIndex < modalData.videos.length ? nextIndex : prev
        })
      }
    },
    [modalData.images, modalData.videos, assetsCollection, answer.id, timeline]
  )

  const resetImageState = useCallback(() => {
    requestAnimationFrame(() => {
      setOnImage(false)
      setPosition({ x: 0, y: 0 })
      timeline.log({
        ref: answer.id,
        type: 'zoom_image_out',
        target: assetsCollection.indexOf(assetsCollection[selectedImageIndex]).toString()
      })
    })
  }, [timeline, answer.id, selectedImageIndex, assetsCollection])

  const onImageEnter = useCallback(() => {
    setOnImage(true)
    timeline.log({
      ref: answer.id,
      type: 'zoom_image_in',
      target: assetsCollection.indexOf(assetsCollection[selectedImageIndex]).toString()
    })
  }, [answer.id, assetsCollection, selectedImageIndex, timeline])

  const onMoveOnImage = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const element = event.currentTarget
    requestAnimationFrame(() => {
      const { left, top, width, height } = element.getBoundingClientRect()
      const centerX = width / 2
      const centerY = height / 2
      const mouseX = event.clientX - left
      const mouseY = event.clientY - top

      setPosition({
        x: -(mouseX - centerX) * 0.75,
        y: -(mouseY - centerY) * 0.75
      })
    })
  }, [])

  const onImageSelect = (index: number) => {
    timeline.log({
      type: 'show_asset',
      ref: answer.id,
      target: assetsCollection.indexOf(assetsCollection[index]).toString(),
      context: 'image'
    })
    setSelectedImageIndex(index)
  }

  const onVideoSelect = (index: number, asset: assetsCollectionElement) => {
    if (index !== selectedVideoIndex) flushPlayingVideo() // same-thumb click: the video keeps playing
    timeline.log({
      type: 'show_asset',
      ref: answer.id,
      target: assetsCollection.indexOf(asset).toString(),
      context: 'video'
    })
    setSelectedVideoIndex(index)
  }

  const onModelSelect = () => {
    timeline.log({
      type: 'show_asset',
      ref: answer.id,
      target: (assetsCollection.length - 1).toString(),
      context: 'model'
    })
  }

  const onTabChange = (event: TabViewTabChangeParams) => {
    if (event.index === currentTabIndex) return // header click on the active tab: nothing closes or opens
    flushPlayingVideo()
    timeline.log({
      ref: answer.id,
      type: `close_${tabAssetType(currentTabIndex).toLowerCase()}_dialog`
    })
    setCurrenTabIndex(event.index)
    timeline.log({
      ref: answer.id,
      type: `open_${tabAssetType(event.index).toLowerCase()}_dialog`
    })
  }

  const logVideoEvent = (type: 'play_video' | 'pause_video', collectionIndex: number) =>
    timeline.log({ ref: answer.id, target: collectionIndex.toString(), type })

  const onVideoPlay = () => {
    playingIndexRef.current = assetsCollection.indexOf(modalData.videos[selectedVideoIndex])
    logVideoEvent('play_video', playingIndexRef.current)
  }

  const onVideoPause = () => {
    playingIndexRef.current = null
    logVideoEvent('pause_video', assetsCollection.indexOf(modalData.videos[selectedVideoIndex]))
  }

  // The <video> unmounts on tab switch and modal close and remounts on video switch, so its own pause never reaches React.
  const flushPlayingVideo = () => {
    if (null === playingIndexRef.current) return
    logVideoEvent('pause_video', playingIndexRef.current)
    playingIndexRef.current = null
  }

  //effects
  useEffect(
    () => () => flushPlayingVideo(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  useEffect(() => {
    let logContext: string
    let targetIndex: number

    switch (currentTabIndex) {
      case 0:
        logContext = 'image'
        targetIndex = assetsCollection.indexOf(modalData.images[selectedImageIndex])
        break
      case 1:
        logContext = 'video'
        targetIndex = assetsCollection.indexOf(modalData.videos[selectedVideoIndex])
        break
      case 2:
        logContext = 'model'
        targetIndex = assetsCollection.indexOf(modalData.models[0])
        break
      default:
        return
    }

    timeline.log({
      ref: answer.id,
      type: 'show_asset',
      target: targetIndex.toString(),
      context: logContext
    })
  }, [
    currentTabIndex,
    selectedImageIndex,
    selectedVideoIndex,
    modalData.images,
    modalData.videos,
    modalData.models,
    assetsCollection,
    answer.id,
    timeline
  ])

  return (
    <div
      className={`${style.dialogLyaout} ${viewport.mobile ? style.dialogLyaoutMobile : ''}`}
      data-testid="SW-EC-AssetsModal"
      role="dialog"
    >
      <div className={style.dialog} ref={dialogRef}>
        <div className={style.close} onClick={() => onModalClose(activeTabAssetType)}>
          <div className={style.title}>{t('questions', 'ec', 'walmart', 'galleryTitle')}</div>
          <i className="wm-Close" />
        </div>

        <TabView activeIndex={currentTabIndex} onTabChange={onTabChange}>
          <TabPanel
            headerStyle={{ display: modalData.hasImages ? 'block' : 'none' }}
            header={
              <button
                className={style.tabButton}
                dangerouslySetInnerHTML={{ __html: t('questions', 'ec', 'walmart', 'images') }}
              />
            }
          >
            {modalData.hasImages && (
              <div className={style.content}>
                <div className={style.imageActions}>
                  <EmptyLink color="#0053e2">
                    <div className={style.iconWrapper}>
                      <i className="wm-Heart" />
                    </div>
                  </EmptyLink>
                  <EmptyLink color="#0053e2">
                    <button className={style.iconWrapper}>
                      <i className="wm-ZoomOut" />
                    </button>
                  </EmptyLink>
                  <EmptyLink color="#0053e2">
                    <button className={style.iconWrapper}>
                      <i className="wm-ZoomIn" />
                    </button>
                  </EmptyLink>
                </div>
                <div className={style.collection}>
                  {modalData.images.map((asset, index) => (
                    <div className={style.collectionItem} key={`image-${index}`}>
                      {ASSETS_TYPES.IMAGE === getAssetType(asset) && (
                        <button
                          className={`${style.imagePreview} ${
                            index === selectedImageIndex ? style.imagePreviewActive : ''
                          }`}
                          style={{ backgroundImage: `url(${getSmallAssetPath(asset as IImageAsset)})` }}
                          onClick={() => onImageSelect(index)}
                        />
                      )}
                    </div>
                  ))}
                </div>
                {viewport.mobile ? (
                  <>
                    {modalData.images[selectedImageIndex] &&
                      ASSETS_TYPES.IMAGE === getAssetType(modalData.images[selectedImageIndex]) && (
                        <AssetsGate
                          urls={[getBigAssetPath(modalData.images[selectedImageIndex] as IImageAsset, 'transparent')]}
                          loader={<PlateSkeleton className={style.mobileImageSkeleton} />}
                        >
                          <img
                            className={style.mobileImage}
                            alt={
                              (modalData.images[selectedImageIndex] as IImageAsset).alt_text ||
                              t('questions', 'img', 'noAlt')
                            }
                            src={getBigAssetPath(modalData.images[selectedImageIndex] as IImageAsset, 'transparent')}
                            loading="lazy"
                          />
                        </AssetsGate>
                      )}
                  </>
                ) : (
                  <div className={style.image}>
                    {selectedImageIndex > 0 ? (
                      <button className={style.prev} onClick={() => slide(-1, 'images')}>
                        <i className="wm-ChevronLeft" />
                      </button>
                    ) : (
                      <div className={style.placeholder}></div>
                    )}
                    <div
                      className={`${style.bigImage}`}
                      style={{ border: `1px solid ${onImage ? 'rgb(45, 45, 45)' : '#fff'}` }}
                    >
                      {modalData.images[selectedImageIndex] &&
                        ASSETS_TYPES.IMAGE === getAssetType(modalData.images[selectedImageIndex]) && (
                          <AssetsGate
                            urls={[getBigAssetPath(modalData.images[selectedImageIndex] as IImageAsset, 'transparent')]}
                            loader={<PlateSkeleton className={style.imageSkeleton} />}
                          >
                            <div
                              className={style.imageItem}
                              role="img"
                              aria-label={
                                (modalData.images[selectedImageIndex] as IImageAsset).alt_text ||
                                t('questions', 'img', 'noAlt')
                              }
                              style={{
                                backgroundImage: `url(${getBigAssetPath(
                                  modalData.images[selectedImageIndex] as IImageAsset,
                                  'transparent'
                                )})`,
                                backgroundPosition: position
                                  ? `calc(50% + ${position.x}px) calc(50% + ${position.y}px)`
                                  : '50% 50%',
                                transform: `scale(${onImage ? 2.5 : 1})`,
                                maxWidth: '100%'
                              }}
                              onMouseMove={onMoveOnImage}
                              onMouseEnter={onImageEnter}
                              onMouseLeave={resetImageState}
                            />
                          </AssetsGate>
                        )}
                    </div>
                    {selectedImageIndex < modalData.images.length - 1 ? (
                      <button className={style.next} onClick={() => slide(1, 'images')}>
                        <i className="wm-ChevronRight" />
                      </button>
                    ) : (
                      <div className={style.placeholder}></div>
                    )}
                  </div>
                )}
              </div>
            )}
          </TabPanel>

          <TabPanel
            headerStyle={{ display: modalData.hasVideos ? 'block' : 'none' }}
            header={
              <button
                className={style.tabButton}
                dangerouslySetInnerHTML={{ __html: t('questions', 'ec', 'walmart', 'videos') }}
              />
            }
          >
            {modalData.hasVideos && (
              <div className={style.content}>
                <div className={style.imageActions}>
                  <EmptyLink color="#0053e2">
                    <div className={style.iconWrapper}>
                      <i className="wm-Heart" />
                    </div>
                  </EmptyLink>
                </div>
                <div className={style.collection}>
                  {modalData.videos.map((asset, index) => (
                    <div className={style.collectionItem} key={index}>
                      <button
                        className={`${style.imagePreview} ${
                          index === selectedVideoIndex ? style.imagePreviewActive : ''
                        }`}
                        style={{ backgroundImage: `url(${asset.video.thumbnail})` }}
                        onClick={() => onVideoSelect(index, asset)}
                      >
                        <div className={style.playWrapper}>
                          <i className="wm-Play" />
                        </div>
                      </button>
                    </div>
                  ))}
                </div>
                <div className={style.videoWrapper}>
                  {selectedVideoIndex > 0 ? (
                    <button className={style.prev} onClick={() => slide(-1, 'videos')}>
                      <i className="wm-ChevronLeft" />
                    </button>
                  ) : (
                    <div className={style.placeholder}></div>
                  )}
                  {modalData.videos[selectedVideoIndex] && (
                    <video
                      key={selectedVideoIndex} // remount on video->video switch; a reused node can fire the browser's src-change pause into the new index
                      autoPlay
                      muted
                      playsInline
                      className={style.video}
                      src={modalData.videos[selectedVideoIndex].video.url}
                      onPlay={onVideoPlay}
                      onPause={onVideoPause}
                      preload="metadata"
                      controls={true}
                    />
                  )}
                  {selectedVideoIndex < modalData.videos.length - 1 ? (
                    <button className={style.next} onClick={() => slide(1, 'videos')}>
                      <i className="wm-ChevronRight" />
                    </button>
                  ) : (
                    <div className={style.placeholder}></div>
                  )}
                </div>
              </div>
            )}
          </TabPanel>

          <TabPanel
            headerStyle={{ display: modalData.hasModels ? 'block' : 'none' }}
            header={
              <button
                className={style.tabButton}
                dangerouslySetInnerHTML={{ __html: t('questions', 'ec', 'walmart', 'models3d') }}
              />
            }
          >
            {modalData.hasModels && (
              <div className={style.content}>
                <div className={style.imageActions}>
                  <EmptyLink color="#0053e2">
                    <div className={style.iconWrapper}>
                      <i className="wm-Heart" />
                    </div>
                  </EmptyLink>
                </div>
                <div className={style.collection}>
                  {modalData.models.map((asset, index) => (
                    <div className={style.collectionItem} key={`model-${index}`}>
                      <div
                        className={`${style.modelPreviewWrapper} ${style.modelPreviewWrapperActive}`}
                        onClick={event => {
                          event.stopPropagation()
                          onModelSelect()
                        }}
                      >
                        <model-viewer
                          src={asset.file_url}
                          camera-controls={false}
                          interaction-prompt="none"
                          style={{
                            width: viewport.mobile ? '64px' : '117px',
                            height: viewport.mobile ? '64px' : '117px'
                          }}
                        />
                        <div className={style.modelOverlay} />
                      </div>
                    </div>
                  ))}
                </div>

                {viewport.mobile ? (
                  <>
                    <div className={style.modelView}>
                      <model-viewer
                        src={modalData.models[0].file_url}
                        auto-rotate
                        camera-controls={false}
                        interaction-prompt="none"
                        style={{ width: '100%', height: '100%' }}
                      />
                    </div>
                  </>
                ) : (
                  <div className={style.image}>
                    <div className={style.placeholder}></div>
                    <div
                      className={`${style.bigImage}`}
                      style={{ border: `1px solid ${onImage ? 'rgb(45, 45, 45)' : '#fff'}` }}
                    >
                      <div className={style.viewerWrapper}>
                        <model-viewer
                          style={{
                            height: 'calc(100vh - 250px)',
                            width: '100%'
                          }}
                          src={modalData.models[0].file_url}
                          ar
                          camera-controls
                          touch-action="pan-y"
                        />
                      </div>
                    </div>
                    <div className={style.placeholder}></div>
                  </div>
                )}
              </div>
            )}
          </TabPanel>
        </TabView>
      </div>
    </div>
  )
}

export default AssetsModal
